// pages/Community/CommunityChannels.jsx
//
// The "Channels" tab of the Community messaging hub. Per-brand config for the
// multi-channel alert/messaging senders (WhatsApp / Telegram / Discord / Slack /
// Teams) — all at the same level. Consumes /api/alert-channels (config/test).
// Secrets are write-only: the server returns hasX flags, never the value.
//
// Phase 0 of the messaging hub; the Inbox / Discovery / Brand-Voice / Automation
// tabs land in later phases.

import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import addNotification from "helpers/notify";
import { getAlertChannels, saveAlertChannel, testAlertChannel } from "api/alertChannels/alertChannels";
import { getBrandAiConfig, setBrandAiConfig } from "api/messaging/messaging";

// Per-brand AI reply settings (Phase 2): drafts on/off + auto-reply.
const AiReplyConfig = ({ brandSettingsId }) => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    if (!brandSettingsId) return;
    getBrandAiConfig(brandSettingsId).then((r) => setCfg((r && r.data) || { enabled: true, autoReply: false })).catch(() => {});
  }, [brandSettingsId]);
  const update = (patch) => {
    const next = { ...(cfg || {}), ...patch };
    setCfg(next);
    setBrandAiConfig({ brandSettingsId, ...next }).then((r) => { if (r && r.data) setCfg(r.data); })
      .catch(() => addNotification("Could not save AI settings.", "danger"));
  };
  if (!cfg) return null;
  return (
    <div style={{ border: "1px solid #e3e6ec", borderRadius: 10, padding: 16, marginBottom: 16, background: "#f7f9ff" }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}><i className="fas fa-robot mr-2" style={{ color: "#0b3d91" }} />AI Replies</div>
      <div className="text-muted mb-2" style={{ fontSize: "0.78rem" }}>
        Brand-voice replies in the Inbox. The LLM↔Claude provider follows your AI-Operations mode.
      </div>
      <label style={{ display: "block", fontSize: "0.85rem", marginBottom: 4 }}>
        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => update({ enabled: e.target.checked })} style={{ marginRight: 6 }} />
        Enable the "AI Draft" button in the Inbox
      </label>
      <label style={{ display: "block", fontSize: "0.85rem" }}>
        <input type="checkbox" checked={!!cfg.autoReply} onChange={(e) => update({ autoReply: e.target.checked })} style={{ marginRight: 6 }} />
        Auto-reply: AI sends a reply automatically on every inbound message
      </label>
    </div>
  );
};

// Channel definitions: label + the editable fields. `secret` fields are
// write-only (shown as "set / change"); `array` fields edit as comma-separated.
const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: "fab fa-whatsapp", color: "#25D366",
    note: "Business-initiated alerts need an approved Utility template (body \"{{1}}\"). Without one, Test uses Meta's hello_world.",
    fields: [
      { k: "phoneNumberId", label: "Phone number ID" },
      { k: "businessAccountId", label: "WhatsApp Business Account (WABA) ID" },
      { k: "accessToken", label: "Access token", secret: true, has: "hasAccessToken" },
      { k: "recipients", label: "Recipients (comma-separated, E.164 e.g. 14808594697)", array: true },
      { k: "templateName", label: "Template name (optional)" },
      { k: "templateLang", label: "Template language", placeholder: "en_US" },
    ] },
  { key: "telegram", label: "Telegram", icon: "fab fa-telegram", color: "#229ED9",
    note: "Create a bot via @BotFather, add it to your group/channel, and use that chat ID.",
    fields: [
      { k: "chatId", label: "Chat / channel ID" },
      { k: "botToken", label: "Bot token", secret: true, has: "hasBotToken" },
    ] },
  { key: "discord", label: "Discord", icon: "fab fa-discord", color: "#5865F2",
    note: "Channel → Edit → Integrations → Webhooks → New Webhook → Copy URL.",
    fields: [{ k: "webhookUrl", label: "Webhook URL", secret: true, has: "hasWebhookUrl" }] },
  { key: "slack", label: "Slack", icon: "fab fa-slack", color: "#4A154B",
    note: "Use a Slack Incoming Webhook URL.",
    fields: [{ k: "webhookUrl", label: "Webhook URL", secret: true, has: "hasWebhookUrl" }] },
  { key: "teams", label: "Microsoft Teams", icon: "fab fa-microsoft", color: "#6264A7",
    note: "Channel → Connectors → Incoming Webhook → Copy URL.",
    fields: [{ k: "webhookUrl", label: "Webhook URL", secret: true, has: "hasWebhookUrl" }] },
  { key: "sms", label: "SMS (Twilio)", icon: "fas fa-sms", color: "#F22F46",
    note: "Twilio Console → Account SID + Auth Token; From = your Twilio number (E.164).",
    fields: [
      { k: "accountSid", label: "Account SID" },
      { k: "authToken", label: "Auth token", secret: true, has: "hasAuthToken" },
      { k: "from", label: "From number (E.164, e.g. +14805551234)" },
      { k: "recipients", label: "Recipients (comma-separated, E.164)", array: true },
    ] },
  { key: "ntfy", label: "ntfy (alarm push)", icon: "fas fa-bell", color: "#2f855a",
    note: "Self-hosted alarm-style push (priority 5 rings through silent mode). Install the ntfy app, add server + user, subscribe to the topic.",
    fields: [
      { k: "serverUrl", label: "Server URL", placeholder: "https://alerts.prigmar.com" },
      { k: "topic", label: "Topic", placeholder: "prigmar-human-handoff" },
      { k: "token", label: "Access token (tk_…)", secret: true, has: "hasToken" },
    ] },
];

const ChannelCard = ({ def, config, brandSettingsId, onSaved }) => {
  // Local edit buffer seeded from the masked server config. Secret inputs start
  // empty (we only send them when the operator types a new value).
  const [enabled, setEnabled] = useState(!!config.enabled);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEnabled(!!config.enabled);
    const v = {};
    def.fields.forEach((f) => {
      if (f.secret) { v[f.k] = ""; return; }
      v[f.k] = f.array ? (Array.isArray(config[f.k]) ? config[f.k].join(", ") : "") : (config[f.k] || "");
    });
    setVals(v);
  }, [config, def]);

  const setVal = (k, val) => setVals((p) => ({ ...p, [k]: val }));

  const buildPayload = () => {
    const out = { brandSettingsId, channel: def.key, enabled };
    def.fields.forEach((f) => {
      const raw = vals[f.k];
      if (f.secret) { if (raw && raw.trim()) out[f.k] = raw.trim(); return; } // omit empty → keep existing
      if (f.array) { out[f.k] = String(raw || "").split(",").map((s) => s.trim()).filter(Boolean); return; }
      out[f.k] = String(raw || "").trim();
    });
    return out;
  };

  const save = useCallback(async () => {
    setBusy(true);
    try {
      const res = await saveAlertChannel(buildPayload());
      addNotification(`${def.label} settings saved.`, "info");
      onSaved && onSaved(res && res.data);
    } catch (e) {
      addNotification((e && e.message) || `Could not save ${def.label}.`, "danger");
    } finally { setBusy(false); }
  }, [vals, enabled, brandSettingsId, def]); // eslint-disable-line

  const test = useCallback(async () => {
    setBusy(true);
    try {
      const res = await testAlertChannel({ brandSettingsId, channel: def.key });
      if (res && res.success) addNotification(`${def.label} test sent ✅`, "success");
      else {
        const err = (res && res.results && res.results.find((r) => r && r.error)) || {};
        addNotification(`${def.label} test failed: ${err.error || (res && res.error) || "unknown error"}`, "danger");
      }
    } catch (e) {
      addNotification((e && e.message) || `${def.label} test failed.`, "danger");
    } finally { setBusy(false); }
  }, [brandSettingsId, def]);

  return (
    <div style={{ border: "1px solid #e3e6ec", borderRadius: 10, padding: 16, marginBottom: 16, background: "#fff" }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div style={{ fontWeight: 700, color: def.color }}>
          <i className={def.icon} style={{ marginRight: 8 }} />{def.label}
        </div>
        <label style={{ margin: 0, cursor: "pointer", fontSize: "0.85rem" }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} style={{ marginRight: 6 }} />
          Enabled
        </label>
      </div>
      {def.note ? <div className="text-muted mb-2" style={{ fontSize: "0.78rem" }}>{def.note}</div> : null}

      {def.fields.map((f) => (
        <div className="mb-2" key={f.k}>
          <label className="mb-1" style={{ fontSize: "0.78rem", fontWeight: 600 }}>
            {f.label}
            {f.secret && config[f.has] ? <span className="text-success" style={{ marginLeft: 6, fontWeight: 400 }}>• configured (leave blank to keep)</span> : null}
          </label>
          <input
            className="form-control form-control-sm"
            type={f.secret ? "password" : "text"}
            value={vals[f.k] || ""}
            placeholder={f.placeholder || (f.secret && config[f.has] ? "••••••••  (enter to change)" : "")}
            onChange={(e) => setVal(f.k, e.target.value)}
            autoComplete="off"
          />
        </div>
      ))}

      <div className="d-flex" style={{ gap: 8, marginTop: 10 }}>
        <button className="btn btn-sm btn-primary m-0" disabled={busy} onClick={save} style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }}>
          {busy ? "Working…" : "Save"}
        </button>
        <button className="btn btn-sm btn-outline-secondary m-0" disabled={busy} onClick={test} title="Send a test message through this channel">
          Send test
        </button>
      </div>
    </div>
  );
};

const CommunityChannels = ({ brandId: brandIdProp }) => {
  const brandsRedux = useSelector((s) => s.user.brands) || [];
  const [brandId, setBrandId] = useState(brandIdProp || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedBrand = brandsRedux.find((b) => String(b._id) === String(brandId));
  const brandSettingsId = selectedBrand && selectedBrand.brandSettings && selectedBrand.brandSettings._id;

  const load = useCallback(async (bsid) => {
    if (!bsid) { setData(null); return; }
    setLoading(true);
    try {
      const res = await getAlertChannels(bsid);
      setData((res && res.data) || {});
    } catch (e) {
      addNotification((e && e.message) || "Could not load channels.", "danger");
      setData({});
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (brandSettingsId) load(brandSettingsId); }, [brandSettingsId, load]);

  return (
    <div style={{ maxWidth: 720 }}>
      <h5 style={{ fontWeight: 700 }}>Messaging Channels</h5>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        Connect a brand's messaging channels. Alerts (e.g. an instant-meeting request) and
        future inbox/automation features fan out to every <strong>enabled</strong> channel.
      </p>

      <div className="mb-3" style={{ maxWidth: 360 }}>
        <label className="mb-1" style={{ fontSize: "0.8rem", fontWeight: 600 }}>Brand</label>
        <select className="form-control form-control-sm" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          <option value="">Select a brand…</option>
          {brandsRedux.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
        </select>
      </div>

      {!brandId ? <div className="text-muted">Pick a brand to configure its channels.</div>
        : !brandSettingsId ? <div className="text-danger">This brand has no settings record yet.</div>
        : loading ? <div className="text-muted">Loading…</div>
        : data ? (
          <div>
            <AiReplyConfig brandSettingsId={brandSettingsId} />
            {CHANNELS.map((def) => (
              <ChannelCard key={def.key} def={def} config={(data && data[def.key]) || {}} brandSettingsId={brandSettingsId} onSaved={() => load(brandSettingsId)} />
            ))}
          </div>
        ) : null}
    </div>
  );
};

export default CommunityChannels;
